"use client";

/** Client-side + rules-aligned KYC validators for secure auto-approval. */

export function normalizeUpiId(raw: string) {
  return raw.trim().toLowerCase().replace(/\s+/g, "");
}

/** NPCI VPA shape: local-part @ handle (paytm, ybl, oksbi, apl, etc.) */
export function isValidUpiId(raw: string) {
  const upi = normalizeUpiId(raw);
  if (upi.length < 5 || upi.length > 100) return false;
  if (!upi.includes("@")) return false;
  // Reject emails mistaken as UPI, and incomplete handles.
  if (/\.(com|in|org|net)$/i.test(upi)) return false;
  return /^[a-z0-9][a-z0-9._-]{1,255}@[a-z][a-z0-9]{1,63}$/.test(upi);
}

export function isValidIfsc(raw: string) {
  return /^[A-Z]{4}0[A-Z0-9]{6}$/i.test(raw.trim());
}

export function isValidPan(raw: string) {
  const pan = raw.trim().toUpperCase();
  if (!pan) return true; // optional
  return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan);
}

export function isValidPhone(raw: string) {
  const digits = raw.replace(/\D/g, "");
  return digits.length === 10 || (digits.length === 12 && digits.startsWith("91"));
}

export function isValidPincode(raw: string) {
  return /^[1-9][0-9]{5}$/.test(raw.trim());
}

export function isValidEmail(raw: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());
}

export type AutoVerifyInput = {
  name: string;
  phone: string;
  email: string;
  dateOfBirth: string;
  addressLine: string;
  city: string;
  state: string;
  pincode: string;
  shopName: string;
  idNumber: string;
  panNumber: string;
  ifsc: string;
  upiId: string;
  accountHolderName: string;
  accountNumber: string;
  emergencyName: string;
  emergencyPhone: string;
  services: string[];
  vehicleTypes: string[];
  hasIdDoc: boolean;
  hasSelfie: boolean;
  selfieFromCamera: boolean;
  acceptedPartnerCode: boolean;
};

export type VerifyCheck = { key: string; label: string; ok: boolean };

export function runAutoVerification(input: AutoVerifyInput): { ok: boolean; checks: VerifyCheck[]; firstError?: string } {
  const checks: VerifyCheck[] = [
    { key: "identity_name", label: "Legal name", ok: input.name.trim().length >= 3 },
    { key: "phone", label: "Phone", ok: isValidPhone(input.phone) },
    { key: "email", label: "Email", ok: isValidEmail(input.email) },
    { key: "dob", label: "Date of birth", ok: Boolean(input.dateOfBirth) },
    { key: "address", label: "Address", ok: input.addressLine.trim().length >= 8 && input.city.trim().length >= 2 && input.state.trim().length >= 2 },
    { key: "pincode", label: "Pincode", ok: isValidPincode(input.pincode) },
    { key: "shop", label: "Shop / work name", ok: input.shopName.trim().length >= 2 },
    { key: "id_number", label: "ID number", ok: input.idNumber.replace(/\s+/g, "").length >= 4 },
    { key: "pan", label: "PAN format", ok: isValidPan(input.panNumber) },
    { key: "ifsc", label: "IFSC", ok: isValidIfsc(input.ifsc) },
    { key: "upi", label: "UPI ID", ok: isValidUpiId(input.upiId) },
    { key: "account", label: "Bank account", ok: input.accountHolderName.trim().length >= 3 && input.accountNumber.replace(/\D/g, "").length >= 8 },
    { key: "emergency", label: "Emergency contact", ok: input.emergencyName.trim().length >= 2 && isValidPhone(input.emergencyPhone) },
    { key: "services", label: "Services & vehicles", ok: input.services.length > 0 && input.vehicleTypes.length > 0 },
    { key: "id_doc", label: "Government ID document", ok: input.hasIdDoc },
    { key: "selfie_live", label: "Live camera selfie", ok: input.hasSelfie && input.selfieFromCamera },
    { key: "partner_code", label: "Partner Code accepted", ok: input.acceptedPartnerCode },
  ];
  const failed = checks.find((check) => !check.ok);
  return { ok: !failed, checks, firstError: failed?.label };
}

export const PARTNER_DRAFT_KEY = "mistry-bhai-partner-kyc-draft-v1";
